import { FC } from "react";
import {
	Stack,
	TextField,
	TextFieldProps,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

/**
 * Date + time picker aligned with Identity’s CustomDateTimePickerField pattern:
 * desktop DateTimePicker vs MobileDateTimePicker on small screens.
 *
 * improve-ui uses MUI X v5 + Dayjs; identity-web uses MUI X v7 + date-fns.
 */
export interface CustomDateTimePickerFieldProps {
	label: string;
	value: Dayjs | null;
	onChange: (value: Dayjs | null) => void;
	disabled?: boolean;
	/** Earliest selectable calendar day (default: start of today). */
	minDate?: Dayjs;
	dataTestId?: string;
	"aria-label"?: string;
}

/** dayjs parse/display; minutes token must stay lowercase `mm`. */
const DATE_TIME_INPUT_FORMAT = "MM/DD/YYYY hh:mm A";
/** Placeholder only (not a dayjs pattern). */
const DATE_TIME_PLACEHOLDER = "MM/DD/YYYY HH:MM AM/PM";

const CustomDateTimePickerField: FC<CustomDateTimePickerFieldProps> = ({
	label,
	value,
	onChange,
	disabled = false,
	minDate,
	dataTestId,
	"aria-label": ariaLabel,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const Picker = isMobile ? MobileDateTimePicker : DateTimePicker;
	const min = minDate ?? dayjs().startOf("day");

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Stack sx={{ minWidth: 220 }}>
				<Picker
					label={label}
					value={value}
					disabled={disabled}
					onChange={onChange}
					minDate={min}
					ampm={true}
					inputFormat={DATE_TIME_INPUT_FORMAT}
					renderInput={(params: TextFieldProps) => (
						<TextField
							{...params}
							fullWidth
							inputProps={{
								...params.inputProps,
								placeholder: DATE_TIME_PLACEHOLDER,
								...(dataTestId ? { "data-testid": dataTestId } : {}),
								...(ariaLabel ? { "aria-label": ariaLabel } : {}),
							}}
							InputLabelProps={{
								...params.InputLabelProps,
								shrink: true,
							}}
						/>
					)}
				/>
			</Stack>
		</LocalizationProvider>
	);
};

export default CustomDateTimePickerField;
